/* eslint-disable camelcase */
import {
    useCallback, useEffect, useRef, useState
} from "react";
import { SquareButton } from "@themuse/design-system";
import { trackSnowplowImpressions, triggerSnowplowClickEvent } from "@themuse/snowplow-js-client";
import { useAppState } from "hooks/AppContext";
import { fetchJobs } from "utils/api";
import { FolderIcon } from "./Icons";
import { JobCardListItem } from "./JobCardListItem";
import styles from "./JobCardList.module.scss";

/**
 * The Tiles function takes in an array of job objects and a user object as props.
 * It renders a list of JobTile components.
 */
const Tiles = (props: { jobs: any[], user: any; }) => {
    const { jobs, user } = props;
    const wrapper = useRef();

    useEffect(() => {
        trackSnowplowImpressions(wrapper.current || undefined);
    }, [jobs?.length]);

    return (
        <ul className={styles.jobCardList} ref={wrapper}>
            {jobs.map((job, index) => (
                <JobCardListItem
                    key={job?.hit?.id}
                    job={job}
                    userLocationDma={user?.dma}
                    order={index}
                />
            ))}
        </ul>
    );
};

/**
 * The Empty function renders a message indicating that a company currently has no jobs.
 */
const Empty = () => (
    <div className={styles.empty}>
        <FolderIcon {...{ className: styles.folder }} />
        <p className={styles.subtext}>This company currently has no jobs</p>
    </div>
);

/**
 * The JobList function is a React component that fetches job data from an API and displays it in a list.
 * It also handles loading state and displays a button with a link to view more | all jobs.
 */
const JobCardList = () => {
    const { company, user } = useAppState();
    const { short_name } = company;
    const [jobs, setJobs] = useState([]);
    const [isLoading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        const data = await fetchJobs({ company_short_name: short_name });
        setJobs(data?.hits ?? []);
        setLoading(false);
    }, [short_name]);

    useEffect(() => {
        fetchData();
    }, []);

    const ctaText = jobs.length ? "VIEW ALL JOBS" : "VIEW JOBS FROM OTHER COMPANIES";
    const ctaUrl = jobs.length ? `/hiring/company/${company.short_name}` : "/hiring";

    const CTATracking = {
        sp_click_component: "button",
        sp_click_button_type: "job_search",
        sp_click_button_text: ctaText,
        sp_click_position: "job_section",
        sp_click_target_page_section: "search",
        sp_tile_type: null,
        sp_tile_entity_id: null,
        sp_search_id: null
    };

    if (isLoading) return <p>Loading...</p>;

    return (
        <>
            {jobs.length ? <Tiles {...{ jobs, user }} /> : <Empty />}
            <SquareButton
                color="secondary-100"
                className={styles.searchButton}
                href={ctaUrl}
                onClick={() => triggerSnowplowClickEvent(CTATracking)}
                type="button"
            >
                {ctaText}
            </SquareButton>
        </>
    );
};
JobCardList.displayName = "JobCardList";

export { JobCardList };
